import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../../assets/css/ProfileSecurity.css";
import PrimaryButton from "../../components/common/PrimaryButton";
import SecondaryButton from "../../components/common/SecondaryButton";
import ReusableTextField from "../../components/common/ReusableTextField";
import { SECURITY_CONSTANTS } from "../../constants/default";
import UserService from "../../services/UserService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


export default function ProfileSecurity() {
  const [option, setOption] = useState("password"); // selected security option (userid/password/phone/email)
  const [otpVerified, setOtpVerified] = useState(false); // control flow
  const [otpSent, setOtpSent] = useState(false); // track if OTP is sent
  const [otp, setOtp] = useState(""); // store OTP input
  const [otpOption, setOtpOption] = useState("email"); // "phone" or "email"
  // ----- OTP UI states -----
  const [loadingSendOtp, setLoadingSendOtp] = useState(false);
  const [loadingVerifyOtp, setLoadingVerifyOtp] = useState(false);
  const [timer, setTimer] = useState(0); // seconds remaining for resend
  const [attempts, setAttempts] = useState(0); // number of failed verify attempts
  const maxAttempts = 3;
  const lockDurationMs = 60 * 1000; // lock for 60 seconds when max attempts reached
  const [lockUntil, setLockUntil] = useState(null); // timestamp when lock ends
  const location = useLocation();
  const navigate = useNavigate();
  const appId = process.env.REACT_APP_DEFAULT_APP_ID;

  // FORM DATA STATE
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    email: "",
    currentNewPassword: "",
    newEmail: "",
    newPhoneNumber: ""
  });

  const [userInfo, setUserInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    whatsAppNo: "",
    businessDetails: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };



  // Fetch profile once
  useEffect(() => {
    (async () => {
      try {
        const response = await UserService.fetchUserProfile();
        if (response.data?.success && response.data.data) {
          const userDetails = response.data.data;
          setUserInfo({
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
            email: userDetails.email,
            whatsAppNo: userDetails.whatsAppNo,
            businessDetails: userDetails.businessDetails,
          });
        } else {
          console.error("API did not return expected data format");
        }
      } catch (error) {
        console.error("Error fetching profile details:", error);
      }
    })();
  }, []);

  // Timer countdown effect
  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(interval);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  // Unlock when lockUntil expires
  useEffect(() => {
    if (!lockUntil) return;
    const checkInterval = setInterval(() => {
      if (Date.now() >= lockUntil) {
        setLockUntil(null);
        setAttempts(0); // reset attempts when lock expires
        clearInterval(checkInterval);
      }
    }, 500);
    return () => clearInterval(checkInterval);
  }, [lockUntil]);

  const isLocked = () => {
    return lockUntil && Date.now() < lockUntil;
  };

  // ----- OTP: Send -----
  const handleSendOtp = async () => {
    // If currently locked, show remaining lock seconds
    if (isLocked()) {
      const secsLeft = Math.ceil((lockUntil - Date.now()) / 1000);
      alert(`Too many attempts. Try again in ${secsLeft} second(s).`);
      return;
    }

    setLoadingSendOtp(true);

    const payload = {
      email: otpOption === "email" ? userInfo?.email : '',
      whatsAppNo: otpOption === "phone" ? userInfo?.whatsAppNo : '',
      accountVerificationType: otpOption === "email" ? 1 : 2,
      accountId: appId,
    };

    try {
      await UserService.sendProfileOtp(payload);
      setOtpSent(true);
      setTimer(30);      // user must wait 30s before resend
      setAttempts(0);    // reset attempts when new OTP is sent
      setLockUntil(null);
      setOtp("");        // clear previous otp input
    } catch (error) {
      setOtpSent(false);
      const apiMessage = error?.response?.data?.message || error?.message || "Please try again.";
      alert(apiMessage);
    } finally {
      setLoadingSendOtp(false);
    }
  };

  // ----- OTP: Verify -----
  const handleOtpSubmit = async () => {
    if (isLocked()) {
      const secsLeft = Math.ceil((lockUntil - Date.now()) / 1000);
      alert(`Too many attempts. Try again in ${secsLeft} second(s).`);
      return;
    }

    if (!otp.trim()) {
      alert("Please enter OTP before continuing.");
      return;
    }

    setLoadingVerifyOtp(true);

    const payload = {
      email: otpOption === "email" ? userInfo?.email : "",
      whatsAppNo: otpOption === "phone" ? userInfo?.whatsAppNo : "",
      accountVerificationType: otpOption === "email" ? 1 : 2,
      accountId: appId,
      otpNumber: otp,
    };

    try {
      await UserService.verifyProfileOtp(payload);

      // OTP success
      setOtpVerified(true);
      setAttempts(0);
      setLockUntil(null);
      setOtp("");

      alert("OTP Verified Successfully!");
    } catch (error) {
      let message = "";

      setAttempts((prev) => {
        const newAttempts = prev + 1;

        if (newAttempts >= maxAttempts) {
          setLockUntil(Date.now() + lockDurationMs);
          message = "Maximum attempts reached. You are locked for 60 seconds.";
        } else {
          message =
            error?.response?.data?.message ||
            error?.message ||
            "Invalid OTP. Please try again.";
        }

        return newAttempts;
      });

      // 🔥 ALERT moved outside state update to avoid double-click issue
      setTimeout(() => {
        alert(message);
      }, 0);
    } finally {
      setLoadingVerifyOtp(false);
    }
  };



  // ----- Other handlers (kept simple) -----
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (option === SECURITY_CONSTANTS.CHANGE_PASSWORD) {
      if (formData.password !== formData.confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
      const payload = {
        password: formData.password,
        AppId: appId
      };

      try {
        await UserService.updateUserPassword(payload);
        toast.success("Your password has been updated successfully!");
        setTimeout(() => navigate("/signin"), 3000);
      } catch (error) {
        console.error("Change password failed:", error);
        const apiMessage = error.response?.data?.message || "Change password failed. Please try again.";
        toast.error(apiMessage);
      }
    }

    if (option === SECURITY_CONSTANTS.CHANGE_PHONE) {
      const payload = {
        WhatsAppNo: formData.newPhoneNumber,
        AppId: appId,
      };
      try {
        await UserService.updateUserPhone(payload);
        toast.success("Your phone number has been updated successfully!");
        setFormData((prev) => ({ ...prev, newPhoneNumber: "" }));
      } catch (error) {
        console.error("Change phone failed:", error);
        const apiMessage = error.response?.data?.message || "Change phone failed. Please try again.";
        toast.error(apiMessage);
      }
    }

    if (option === SECURITY_CONSTANTS.CHANGE_EMAIL) {
      const payload = {
        email: formData.newEmail,
        AppId: appId
      };

      try {
        await UserService.updateUserEmail(payload);
        toast.success("Your email has been updated successfully!");
        setTimeout(() => navigate("/signin"), 3000);
      } catch (error) {
        console.error("Change Email failed:", error);
        const apiMessage = error.response?.data?.message || "Change email failed. Please try again.";
        toast.error(apiMessage);
      }
    }
  };

  const handleCancel = () => {
    setOption("");
  };

  // UI helpers
  const attemptsLeft = Math.max(0, maxAttempts - attempts);
  const lockSecondsLeft = lockUntil ? Math.ceil((lockUntil - Date.now()) / 1000) : 0;

  return (
    <div className="security-container">
      {/* Main Content */}
      <div className="main-content">
        {!otpVerified ? (
          /* OTP Section (shown first) */
          <div className="otp-section">
            <p>{SECURITY_CONSTANTS.VERIFY_IDENTITY}</p>

            {/* Show attempt / lock messages */}
            <div style={{ marginBottom: 8 }}>
              {isLocked() ? (
                <div className="lock-message" aria-live="polite">
                  Too many incorrect attempts. Try again in {lockSecondsLeft} second(s).
                </div>
              ) : otpSent && attempts > 0 ? (
                <div className="attempts-message" aria-live="polite">
                  Incorrect attempts: {attempts}. Attempts left: {attemptsLeft}.
                </div>
              ) : null}
            </div>

            {/* Radio + Send OTP */}
            {!otpSent && (
              <div className="otp-radio-group">
                <label>
                  <input
                    type="radio"
                    name="otpOption"
                    value="phone"
                    checked={otpOption === "phone"}
                    onChange={(e) => setOtpOption(e.target.value)}
                  />
                  {SECURITY_CONSTANTS.SEND_TO_PHONE}
                </label>

                <label>
                  <input
                    type="radio"
                    name="otpOption"
                    value="email"
                    checked={otpOption === "email"}
                    onChange={(e) => setOtpOption(e.target.value)}
                  />
                  {SECURITY_CONSTANTS.SEND_TO_EMAIL}
                </label>

                <button
                  type="button"
                  className="send-otp-btn"
                  onClick={handleSendOtp}
                  disabled={loadingSendOtp || timer > 0 || isLocked()}
                >
                  {loadingSendOtp ? "Sending..." : (timer > 0 ? `Resend in ${timer}s` : "Send OTP")}
                </button>
              </div>
            )}

            {otpSent && (
              <div className="otp-input-section">
                <h4>{SECURITY_CONSTANTS.RECEIVED_PASSCODE}</h4>
                <p>
                  Enter the passcode we have sent to{" "}
                  {otpOption === "phone"
                    ? SECURITY_CONSTANTS.ENTER_PASSCODE_PHONE(userInfo?.whatsAppNo)
                    : SECURITY_CONSTANTS.ENTER_PASSCODE_EMAIL(userInfo?.email)}
                </p>

                <input
                  type="text"
                  className="otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  disabled={isLocked()}
                />

                <button
                  type="button"
                  className="continue-btn"
                  onClick={handleOtpSubmit}
                  disabled={loadingVerifyOtp || isLocked()}
                >
                  {loadingVerifyOtp ? "Verifying..." : SECURITY_CONSTANTS.CONTINUE}
                </button>
              </div>
            )}

            {/* Resend option / info */}
            {otpSent && (
              <p className="resend-text">
                {SECURITY_CONSTANTS.RESEND_TEXT}{" "}
                {timer > 0 ? (
                  <span>{`(Resend available in ${timer}s)`}</span>
                ) : (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!isLocked()) handleSendOtp();
                      else {
                        const secs = Math.ceil((lockUntil - Date.now()) / 1000);
                        alert(`Locked. Try again in ${secs} second(s).`);
                      }
                    }}
                  >
                    {SECURITY_CONSTANTS.RESEND_LINK}
                  </a>
                )}
              </p>
            )}
          </div>
        ) : (
          /* After OTP is verified: show the 3 vertical radio options + forms */
          <>
            {/* <h3>Change Security Details</h3> */}
            <div className="radio-section">
              <label>
                <input
                  type="radio"
                  name="option"
                  value="password"
                  checked={option === "password"}
                  onChange={(e) => setOption(e.target.value)}
                />
                Password
              </label>

              <label>
                <input
                  type="radio"
                  name="option"
                  value="phone"
                  checked={option === "phone"}
                  onChange={(e) => setOption(e.target.value)}
                />
                Phone Number
              </label>

              <label>
                <input
                  type="radio"
                  name="option"
                  value="email"
                  checked={option === "email"}
                  onChange={(e) => setOption(e.target.value)}
                />
                Email
              </label>
            </div>

            {/* PASSWORD FORM */}
            {option === "password" && (
              <>
                <h5 style={{ fontWeight: 600 }}>Change Password</h5>
                <form className="form" onSubmit={handleFormSubmit}>
                  <ReusableTextField
                    name="password"
                    label="New Password"
                    required
                    placeholder="Enter your new password"
                    value={formData.password}
                    onChange={handleInputChange}
                  />

                  <ReusableTextField
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                  />

                  <div className="buttons">
                    <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
                    <PrimaryButton type="submit">Save</PrimaryButton>
                  </div>
                  <p className="note">Cancel will reset the process</p>
                </form>
              </>
            )}

            {/* PHONE FORM */}
            {option === "phone" && (
              <>
                <h5 style={{ fontWeight: 600 }}>Change Phone Number</h5>
                <form className="form" onSubmit={handleFormSubmit}>
                  <ReusableTextField
                    name="newPhoneNumber"
                    label="New Phone Number"
                    required
                    placeholder="Enter new phone number"
                    value={formData.newPhoneNumber}
                    onChange={handleInputChange}
                  />

                  <div className="buttons">
                    <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
                    <PrimaryButton type="submit">Save</PrimaryButton>
                  </div>
                  <p className="note">Cancel will reset the process</p>
                </form>
              </>
            )}

            {/* EMAIL FORM */}
            {option === "email" && (
              <>
                <h5 style={{ fontWeight: 600 }}>Change Email</h5>
                <form className="form" onSubmit={handleFormSubmit}>
                  <ReusableTextField
                    name="newEmail"
                    label="New Email"
                    required
                    placeholder="Enter new email"
                    value={formData.newEmail}
                    onChange={handleInputChange}
                  />

                  <div className="buttons">
                    <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
                    <PrimaryButton type="submit">Save</PrimaryButton>
                  </div>
                  <p className="note">Cancel will reset the process</p>
                </form>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
